/*
  Окно «Внести оплату» (20.10.2026, решение владельца: вариант «а»).

  Человек сам называет месяц и услугу, к которым относится оплата: она уменьшает долг
  именно этой строки начислений. Автоматического распределения на самые старые месяцы
  нет - это правовое допущение о порядке погашения, и принимает его юрист, а не система.

  Выбор идёт из начислений с долгом: оплатить можно только то, что начислено. Нет
  начисления - окно говорит об этом словами и ведёт к его внесению, а не молчит.
*/

import { useCallback, useState } from 'react'

import { api } from '../../api/client.js'
import { chargeMonth, money, today } from '../../api/format.js'
import { Empty, Field, Loading, Notice } from '../Common.jsx'
import PageBar from '../PageBar.jsx'
import Dropdown from '../Select.jsx'
import usePagedList from '../usePagedList.js'
import FormDialog from './FormDialog.jsx'
import { asDecimal, isMoney } from './money.js'

function titleOf(charge) {
  return `${chargeMonth(charge.period)} - ${charge.service}, долг ${money(charge.debt)}`
}

export default function PaymentDialog({ accountId, onSaved, onClose }) {
  // Выбор строки, к которой отнести оплату, - страницами сервера (23.10.2026, ревью). Прежде
  // строки с долгом брались одной страницей до 500, и у счёта с большим числом строк старые
  // месяцы молча пропадали из выбора. Теперь под списком - та же панель страниц, что у
  // таблиц: «Назад - Вперёд» и число строк с долгом.
  const load = useCallback(
    (window) => api.charges(accountId, { ...window, with_debt: true }),
    [accountId],
  )
  const { rows, error, bar } = usePagedList(load)
  // Выбранная строка хранится целиком: при переходе на другую страницу выбор не теряется.
  const [chosen, setChosen] = useState(null)
  const [form, setForm] = useState({ paidOn: today(), amount: '', payer: '', purpose: '' })
  const set = (name) => (event) => setForm({ ...form, [name]: event.target.value })

  const open = (rows ?? []).filter((row) => Number(row.debt) > 0)
  const options = Object.fromEntries(open.map((row) => [row.charge_id, titleOf(row)]))
  if (chosen && !options[chosen.charge_id]) options[chosen.charge_id] = titleOf(chosen)
  const choose = (chargeId) => setChosen(open.find((row) => row.charge_id === chargeId) ?? chosen)

  function submit() {
    return api.addPayment(accountId, {
      paid_on: form.paidOn,
      amount: asDecimal(form.amount),
      period: chosen.period,
      service: chosen.service,
      payer: form.payer.trim() || null,
      purpose: form.purpose.trim() || null,
    })
  }

  return (
    <FormDialog
      title="Внести оплату"
      submitLabel="Внести оплату"
      canSubmit={Boolean(chosen) && isMoney(form.amount) && form.paidOn !== ''}
      onSubmit={submit}
      onSaved={onSaved}
      onClose={onClose}
    >
      <Notice error={error} />
      {rows === null && !error ? <Loading what="Загружаем начисления" /> : null}
      {rows !== null && bar.total === 0 ? (
        <Empty title="Нет начислений с долгом">
          Оплату относят к начислению: сначала внесите начисление на вкладке «Начисления».
        </Empty>
      ) : null}
      {bar.total > 0 ? (
        <>
          <div className="row">
            <div className="field field--l">
              <span className="field__label" id="payment-charge-label">
                К какому начислению относится оплата
              </span>
              <Dropdown
                value={chosen?.charge_id ?? ''}
                onChange={choose}
                options={options}
                empty="выберите месяц и услугу"
                labelledBy="payment-charge-label"
              />
            </div>
          </div>
          <PageBar {...bar} what="строк с долгом" />
          <div className="row">
            <Field label="Дата оплаты" width="s">
              <input
                className="field__input"
                type="date"
                max={today()}
                value={form.paidOn}
                onChange={set('paidOn')}
              />
            </Field>
            <Field label="Сумма, ₽" width="s">
              <input
                className="field__input"
                inputMode="decimal"
                value={form.amount}
                onChange={set('amount')}
                placeholder="0,00"
              />
            </Field>
            <Field label="Плательщик" width="m">
              <input
                className="field__input"
                value={form.payer}
                maxLength={255}
                onChange={set('payer')}
              />
            </Field>
          </div>
          <div className="row">
            <Field label="Назначение платежа" width="l">
              <input
                className="field__input"
                value={form.purpose}
                maxLength={500}
                onChange={set('purpose')}
              />
            </Field>
          </div>
        </>
      ) : null}
    </FormDialog>
  )
}
